import React, { EventHandler, useCallback, useState } from 'react';
import { makeCSS } from '../../../utils/makeCSS';
import { cx } from '@emotion/css';
import { Typography } from '../../atoms/Typography';
import Truncate from '../../utility/Truncate';

/**
 * A visual indicator for the different options of rating.
 * We use this component for the rating of an asset.
 */
export interface SelectOptionProps extends React.PropsWithChildren {
  /** the selected value */
  value: string;
  /** user facing version of the value */
  label?: string;
  onSelect?: (el) => void;
  onClick?: EventHandler<React.MouseEvent>;
  /**Whether its active/responsive or not */
  disabled?: boolean;
  /** start `InputAdornment` for input component. */
  prefix?: React.ReactNode;
  /** start `InputAdornment` for input component. */
  startAdornment?: React.ReactNode;
  /** end `InputAdornment` for input component */
  endAdornment?: React.ReactNode;
  active?: boolean;
}

export const useOptionCSS = makeCSS(({ theme }) => {
  return {
    root: {
      userSelect: 'none',
      cursor: 'pointer',
      display: 'flex',
      width: 'calc(100% - 2px)',
      height: theme.spacing[10],
      gap: theme.spacing[2],
      padding: theme.spacing[2],
      alignItems: 'center',
      flexShrink: 0,
      borderRadius: theme.borderRadius.lg,
      boxSizing: 'border-box',
      '&:hover': {
        background: theme.color.overlay.hovered
      },
      '&:active': {
        background: theme.color.overlay.pressed
      },
      '&.disabled': {
        cursor: 'default',
        opacity: 0.4,
        '&:hover': {
          background: 'none'
        }
      }
    },
    spacing: {
      width: 'inherit'
    },
    endAdornment: {
      marginLeft: 'auto'
    },
    active: {
      background: theme.color.overlay.pressed
    }
  };
});

export const Option = (props: SelectOptionProps) => {
  const {
    value = 'pending',
    label,
    startAdornment,
    endAdornment,
    onClick,
    disabled,
    children,
    active,
    onSelect
  } = props;

  const [container, setContainer] = useState<HTMLDivElement>(null);

  const str = label || value.trim().charAt(0).toUpperCase() + value.slice(1);
  const padding = getPadding(container);
  const containerWidth = container?.getBoundingClientRect().width;

  const classes = useOptionCSS();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      onSelect?.(e);
      onClick?.(e);
    },
    [onSelect, onClick]
  );

  const startAdornmentWidth =
    container?.childElementCount > 1 && startAdornment
      ? container.firstElementChild.clientWidth
      : 0;
  const endAdornmentWidth =
    container?.childElementCount > 2 && endAdornment
      ? container.lastElementChild.clientWidth
      : 0;

  const textWidth =
    containerWidth - padding - startAdornmentWidth - endAdornmentWidth;

  return (
    <div
      className={cx(
        classes.root,
        {
          disabled: disabled
        },
        active && classes.active
      )}
      onClick={handleClick}
      ref={setContainer}
      data-pw={value + '__option'}
      role="option"
    >
      {startAdornment}
      <Typography variant="md" colorVariant="01" weight="body">
        <div className={`${classes.spacing}`}>
          {children ? (
            children
          ) : container ? (
            <Truncate
              str={str}
              widthLimit={textWidth}
              toolTip={true}
              cachePrefix="option"
            />
          ) : (
            str
          )}
        </div>
      </Typography>
      {endAdornment ? (
        <div className={classes.endAdornment}>{endAdornment}</div>
      ) : null}
    </div>
  );
};
const toNumber = (wid: string | number) =>
  typeof wid === 'number' ? wid : Number((wid || '').replace('px', ''));
const getPadding = (div: Element) => {
  if (!div) return 0;
  const { paddingLeft, paddingRight } = window.getComputedStyle(div);
  return toNumber(paddingLeft) + toNumber(paddingRight);
};
